import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { AVATAR_REQUIRED_FIELDS, countAvatarFields } from "@/lib/avatarFlow";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { ArrowRight, Trash2 } from "lucide-react";

// "Continuar rascunho (x/12) ou descartar?" — shown when a one-tap avatar
// creation hits an existing draft (409). Fetches the draft to display progress.
export default function DraftResumeSheet({
  draftId,
  open,
  onOpenChange,
  onContinue,
  onDiscard,
}) {
  const [filled, setFilled] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !draftId) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/avatars/${draftId}`);
        if (alive) setFilled(countAvatarFields(data));
      } catch {
        // Keep filled=null: the UI omits the counter rather than showing a
        // false "0/12" that would nudge the user toward a destructive discard.
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, draftId]);

  const total = AVATAR_REQUIRED_FIELDS.length;

  const handleDiscard = async () => {
    setBusy(true);
    try {
      await onDiscard();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        data-testid="draft-resume-sheet"
        className="bg-brand-surface border-brand-line rounded-t-3xl"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-2xl text-brand-ink">
            Você tem um rascunho em andamento
          </SheetTitle>
          <SheetDescription className="text-brand-ink2">
            Só é permitido 1 rascunho por vez
            {filled != null && (
              <>
                {" "}
                — <span className="text-brand-rose">{filled}/{total} campos</span> preenchidos
              </>
            )}
            . Continue de onde parou ou descarte para começar de novo.
          </SheetDescription>
        </SheetHeader>
        <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            type="button"
            data-testid="draft-discard-btn"
            onClick={handleDiscard}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-brand-line text-brand-ink2 hover:border-brand-rose hover:text-brand-rose text-[13px] font-ui transition-colors min-h-[44px] disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" />
            Descartar e começar de novo
          </button>
          <button
            type="button"
            data-testid="draft-continue-btn"
            onClick={onContinue}
            disabled={busy}
            className="inline-flex items-center justify-center gap-2 bg-brand-rose hover:bg-brand-roseHover text-white px-5 py-3 rounded-full text-[13px] font-ui transition-colors min-h-[44px] disabled:opacity-50"
          >
            Continuar rascunho
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
